export type CollectionCardTemplateFamily = 'vase' | 'plant-pot' | 'custom';
export type CollectionCardTemplateRowType = 'image' | 'title' | 'description' | 'buttons';
export type CollectionCardTemplateImageAspect =
  | 'square'
  | 'portrait'
  | 'portrait-3-4'
  | 'landscape'
  | 'original';
export type CollectionCardTemplateAlignment = 'left' | 'centre' | 'right';
export type CollectionCardTemplateFontStyle = 'normal' | 'italic';
export type CollectionCardTemplateTextDecoration = 'none' | 'underline';
export type CollectionCardTemplateTextTransform = 'none' | 'uppercase';
export type CollectionCardTemplateButtonAlignment = 'stretch' | 'left' | 'centre';
export type CollectionCardTemplateBorderStyle = 'solid' | 'dashed' | 'dotted';
export type CollectionCardTemplateButtonVariant = 'solid' | 'outline';
export type CollectionCardTemplateHoverAnimation = 'lift-shadow' | 'image-zoom' | 'glow' | 'reveal' | 'tilt';
export type CollectionCardTemplateRevealTarget = 'description' | 'buttons' | 'both';

export interface CollectionCardTypographyStyle {
  fontFamilyKey: string;
  fontSize: number;
  fontWeight: number;
  colour: string;
  fontStyle: CollectionCardTemplateFontStyle;
  textDecorationLine: CollectionCardTemplateTextDecoration;
  textTransform: CollectionCardTemplateTextTransform;
  lineHeight: number;
  letterSpacing: number;
}

export interface CollectionCardButtonStyle {
  gap: number;
  alignment: CollectionCardTemplateButtonAlignment;
  radius: number;
  primaryWidthPercent: number;
  primaryVariant: CollectionCardTemplateButtonVariant;
  primaryBackgroundColour: string;
  primaryTextColour: string;
  secondaryWidthPercent: number;
  secondaryVariant: CollectionCardTemplateButtonVariant;
  secondaryBackgroundColour: string;
  secondaryTextColour: string;
}

export interface CollectionCardTemplate {
  id: string;
  name: string;
  family: CollectionCardTemplateFamily;
  rows: CollectionCardTemplateRowType[];
  imageAspect: CollectionCardTemplateImageAspect;
  imageSizePercent: number;
  imageFocalXPercent: number;
  imageFocalYPercent: number;
  imageOverlayColour: string;
  imageOverlayOpacity: number;
  imageTextGap: number;
  titleDescriptionGap: number;
  contentButtonGap: number;
  titlePresetKey: string;
  descriptionPresetKey: string;
  cardPadding: number;
  cardRadius: number;
  descriptionLines: number;
  textAlign: CollectionCardTemplateAlignment;
  cardBackgroundColor?: string;
  cardBorderColor?: string;
  cardBorderWidth: number;
  cardBorderStyle: CollectionCardTemplateBorderStyle;
  cardShadowColour: string;
  cardShadowOpacity: number;
  cardShadowX: number;
  cardShadowY: number;
  cardShadowBlur: number;
  cardShadowSpread: number;
  hoverEffectEnabled: boolean;
  hoverAnimation: CollectionCardTemplateHoverAnimation;
  hoverLiftY: number;
  hoverShadowY: number;
  hoverShadowBlur: number;
  hoverShadowOpacity: number;
  hoverImageZoomScale: number;
  hoverImageRotateDeg: number;
  hoverGlowColour: string;
  hoverGlowSize: number;
  hoverGlowOpacity: number;
  hoverRevealTarget: CollectionCardTemplateRevealTarget;
  hoverRevealDistance: number;
  hoverTiltDeg: number;
  titleTypography: CollectionCardTypographyStyle;
  descriptionTypography: CollectionCardTypographyStyle;
  buttonTypography: CollectionCardTypographyStyle;
  buttonStyle: CollectionCardButtonStyle;
}

const DEFAULT_CARD_BACKGROUND = '#fafaf9';
const DEFAULT_CARD_BORDER = '#e7e5e4';
const DEFAULT_CARD_BORDER_WIDTH = 1;
const DEFAULT_CARD_BORDER_STYLE: CollectionCardTemplateBorderStyle = 'solid';
const DEFAULT_CARD_SHADOW_COLOUR = '#0f172a';
const DEFAULT_CARD_SHADOW_OPACITY = 0.08;
const DEFAULT_CARD_SHADOW_X = 0;
const DEFAULT_CARD_SHADOW_Y = 12;
const DEFAULT_CARD_SHADOW_BLUR = 26;
const DEFAULT_CARD_SHADOW_SPREAD = -12;
const DEFAULT_TITLE_TYPOGRAPHY: CollectionCardTypographyStyle = {
  fontFamilyKey: 'playfair',
  fontSize: 28,
  fontWeight: 500,
  colour: '#1c1917',
  fontStyle: 'normal',
  textDecorationLine: 'none',
  textTransform: 'none',
  lineHeight: 1.2,
  letterSpacing: 0,
};
const DEFAULT_DESCRIPTION_TYPOGRAPHY: CollectionCardTypographyStyle = {
  fontFamilyKey: 'inter',
  fontSize: 14,
  fontWeight: 400,
  colour: '#57534e',
  fontStyle: 'normal',
  textDecorationLine: 'none',
  textTransform: 'none',
  lineHeight: 1.5,
  letterSpacing: 0,
};
const DEFAULT_BUTTON_TYPOGRAPHY: CollectionCardTypographyStyle = {
  fontFamilyKey: 'inter',
  fontSize: 14,
  fontWeight: 500,
  colour: '#ffffff',
  fontStyle: 'normal',
  textDecorationLine: 'none',
  textTransform: 'none',
  lineHeight: 1.2,
  letterSpacing: 0,
};
const DEFAULT_BUTTON_STYLE: CollectionCardButtonStyle = {
  gap: 8,
  alignment: 'stretch',
  radius: 8,
  primaryWidthPercent: 100,
  primaryVariant: 'solid',
  primaryBackgroundColour: '#f5f5f4',
  primaryTextColour: '#1c1917',
  secondaryWidthPercent: 100,
  secondaryVariant: 'solid',
  secondaryBackgroundColour: '#44403c',
  secondaryTextColour: '#ffffff',
};

function normalizeTypographyStyle(
  value: unknown,
  fallback: CollectionCardTypographyStyle
): CollectionCardTypographyStyle {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    fontFamilyKey:
      typeof raw.fontFamilyKey === 'string' && raw.fontFamilyKey.trim().length > 0
        ? raw.fontFamilyKey.trim()
        : fallback.fontFamilyKey,
    fontSize: clampNumber(raw.fontSize, fallback.fontSize, 10, 64),
    fontWeight: clampNumber(raw.fontWeight, fallback.fontWeight, 300, 800),
    colour:
      typeof raw.colour === 'string' && raw.colour.trim().length > 0
        ? raw.colour.trim()
        : fallback.colour,
    fontStyle: raw.fontStyle === 'italic' ? 'italic' : 'normal',
    textDecorationLine: raw.textDecorationLine === 'underline' ? 'underline' : 'none',
    textTransform: raw.textTransform === 'uppercase' ? 'uppercase' : 'none',
    lineHeight: clampDecimal(raw.lineHeight, fallback.lineHeight, 1, 2.2, 0.05),
    letterSpacing: clampDecimal(raw.letterSpacing, fallback.letterSpacing, -1, 4, 0.1),
  };
}

function normalizeButtonStyle(
  value: unknown,
  fallback: CollectionCardButtonStyle
): CollectionCardButtonStyle {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    gap: clampNumber(raw.gap, fallback.gap, 4, 24),
    alignment:
      raw.alignment === 'left' || raw.alignment === 'centre' || raw.alignment === 'stretch'
        ? raw.alignment
        : fallback.alignment,
    radius: clampNumber(raw.radius, fallback.radius, 4, 24),
    primaryWidthPercent: clampNumber(raw.primaryWidthPercent, fallback.primaryWidthPercent, 40, 100),
    primaryVariant: raw.primaryVariant === 'outline' ? 'outline' : 'solid',
    primaryBackgroundColour:
      typeof raw.primaryBackgroundColour === 'string' && raw.primaryBackgroundColour.trim().length > 0
        ? raw.primaryBackgroundColour.trim()
        : fallback.primaryBackgroundColour,
    primaryTextColour:
      typeof raw.primaryTextColour === 'string' && raw.primaryTextColour.trim().length > 0
        ? raw.primaryTextColour.trim()
        : fallback.primaryTextColour,
    secondaryWidthPercent: clampNumber(raw.secondaryWidthPercent, fallback.secondaryWidthPercent, 40, 100),
    secondaryVariant: raw.secondaryVariant === 'outline' ? 'outline' : 'solid',
    secondaryBackgroundColour:
      typeof raw.secondaryBackgroundColour === 'string' && raw.secondaryBackgroundColour.trim().length > 0
        ? raw.secondaryBackgroundColour.trim()
        : fallback.secondaryBackgroundColour,
    secondaryTextColour:
      typeof raw.secondaryTextColour === 'string' && raw.secondaryTextColour.trim().length > 0
        ? raw.secondaryTextColour.trim()
        : fallback.secondaryTextColour,
  };
}

export const DEFAULT_COLLECTION_CARD_TEMPLATES: CollectionCardTemplate[] = [
  {
    id: 'vase-card',
    name: 'Vase Card',
    family: 'vase',
    rows: ['image', 'title', 'description', 'buttons'],
    imageAspect: 'square',
    imageSizePercent: 100,
    imageFocalXPercent: 50,
    imageFocalYPercent: 50,
    imageOverlayColour: '#0f172a',
    imageOverlayOpacity: 0,
    imageTextGap: 12,
    titleDescriptionGap: 8,
    contentButtonGap: 12,
    titlePresetKey: 'sectionHeading',
    descriptionPresetKey: 'body',
    cardPadding: 18,
    cardRadius: 12,
    descriptionLines: 4,
    textAlign: 'left',
    cardBackgroundColor: DEFAULT_CARD_BACKGROUND,
    cardBorderColor: DEFAULT_CARD_BORDER,
    cardBorderWidth: DEFAULT_CARD_BORDER_WIDTH,
    cardBorderStyle: DEFAULT_CARD_BORDER_STYLE,
    cardShadowColour: DEFAULT_CARD_SHADOW_COLOUR,
    cardShadowOpacity: DEFAULT_CARD_SHADOW_OPACITY,
    cardShadowX: DEFAULT_CARD_SHADOW_X,
    cardShadowY: DEFAULT_CARD_SHADOW_Y,
    cardShadowBlur: DEFAULT_CARD_SHADOW_BLUR,
    cardShadowSpread: DEFAULT_CARD_SHADOW_SPREAD,
    hoverEffectEnabled: false,
    hoverAnimation: 'lift-shadow',
    hoverLiftY: 6,
    hoverShadowY: 18,
    hoverShadowBlur: 30,
    hoverShadowOpacity: 0.16,
    hoverImageZoomScale: 1.05,
    hoverImageRotateDeg: 2,
    hoverGlowColour: '#0f172a',
    hoverGlowSize: 24,
    hoverGlowOpacity: 0.18,
    hoverRevealTarget: 'both',
    hoverRevealDistance: 10,
    hoverTiltDeg: 2,
    titleTypography: DEFAULT_TITLE_TYPOGRAPHY,
    descriptionTypography: DEFAULT_DESCRIPTION_TYPOGRAPHY,
    buttonTypography: DEFAULT_BUTTON_TYPOGRAPHY,
    buttonStyle: DEFAULT_BUTTON_STYLE,
  },
  {
    id: 'plant-pot-card',
    name: 'Plant Pot Card',
    family: 'plant-pot',
    rows: ['image', 'title', 'description', 'buttons'],
    imageAspect: 'portrait',
    imageSizePercent: 100,
    imageFocalXPercent: 50,
    imageFocalYPercent: 50,
    imageOverlayColour: '#0f172a',
    imageOverlayOpacity: 0,
    imageTextGap: 12,
    titleDescriptionGap: 8,
    contentButtonGap: 12,
    titlePresetKey: 'sectionHeading',
    descriptionPresetKey: 'body',
    cardPadding: 20,
    cardRadius: 18,
    descriptionLines: 3,
    textAlign: 'centre',
    cardBackgroundColor: '#f8fafc',
    cardBorderColor: '#dbe4ee',
    cardBorderWidth: DEFAULT_CARD_BORDER_WIDTH,
    cardBorderStyle: DEFAULT_CARD_BORDER_STYLE,
    cardShadowColour: DEFAULT_CARD_SHADOW_COLOUR,
    cardShadowOpacity: DEFAULT_CARD_SHADOW_OPACITY,
    cardShadowX: 0,
    cardShadowY: 16,
    cardShadowBlur: 32,
    cardShadowSpread: -16,
    hoverEffectEnabled: false,
    hoverAnimation: 'lift-shadow',
    hoverLiftY: 6,
    hoverShadowY: 22,
    hoverShadowBlur: 36,
    hoverShadowOpacity: 0.16,
    hoverImageZoomScale: 1.05,
    hoverImageRotateDeg: 2,
    hoverGlowColour: '#0f172a',
    hoverGlowSize: 24,
    hoverGlowOpacity: 0.18,
    hoverRevealTarget: 'both',
    hoverRevealDistance: 10,
    hoverTiltDeg: 2,
    titleTypography: {
      ...DEFAULT_TITLE_TYPOGRAPHY,
      fontSize: 26,
    },
    descriptionTypography: {
      ...DEFAULT_DESCRIPTION_TYPOGRAPHY,
      fontSize: 15,
    },
    buttonTypography: DEFAULT_BUTTON_TYPOGRAPHY,
    buttonStyle: {
      ...DEFAULT_BUTTON_STYLE,
      radius: 12,
    },
  },
];

function clampNumber(value: unknown, fallback: number, min: number, max: number) {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.max(min, Math.min(max, Math.round(next)));
}

function clampDecimal(value: unknown, fallback: number, min: number, max: number, step: number) {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  const clamped = Math.max(min, Math.min(max, next));
  return Math.round(clamped / step) * step;
}

function isTemplateRowType(value: unknown): value is CollectionCardTemplateRowType {
  return value === 'image' || value === 'title' || value === 'description' || value === 'buttons';
}

function normalizeTemplateRows(value: unknown, fallback: CollectionCardTemplateRowType[]) {
  const source = Array.isArray(value) ? value.filter(isTemplateRowType) : [];
  const base = source.length > 0 ? [...source] : [...fallback];
  const seen = new Set<CollectionCardTemplateRowType>();
  const deduped = base.filter((row) => {
    if (seen.has(row)) return false;
    seen.add(row);
    return true;
  });
  (['image', 'title', 'description', 'buttons'] as CollectionCardTemplateRowType[]).forEach((row) => {
    if (!seen.has(row)) deduped.push(row);
  });
  const withoutButtons = deduped.filter((row) => row !== 'buttons');
  return [...withoutButtons, 'buttons'];
}

export function normalizeCollectionCardTemplate(
  value: unknown,
  fallback: CollectionCardTemplate
): CollectionCardTemplate {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    id:
      typeof raw.id === 'string' && raw.id.trim().length > 0
        ? raw.id.trim()
        : fallback.id,
    name:
      typeof raw.name === 'string' && raw.name.trim().length > 0
        ? raw.name.trim()
        : fallback.name,
    family:
      raw.family === 'vase' || raw.family === 'plant-pot' || raw.family === 'custom'
        ? raw.family
        : fallback.family,
    rows: normalizeTemplateRows(raw.rows, fallback.rows),
    imageAspect:
      raw.imageAspect === 'square' ||
      raw.imageAspect === 'portrait' ||
      raw.imageAspect === 'portrait-3-4' ||
      raw.imageAspect === 'landscape' ||
      raw.imageAspect === 'original'
        ? raw.imageAspect
        : fallback.imageAspect,
    imageSizePercent: clampNumber(
      raw.imageSizePercent ?? raw.imageWidthPercent,
      fallback.imageSizePercent,
      30,
      100
    ),
    imageFocalXPercent: clampNumber(raw.imageFocalXPercent, fallback.imageFocalXPercent, 0, 100),
    imageFocalYPercent: clampNumber(raw.imageFocalYPercent, fallback.imageFocalYPercent, 0, 100),
    imageOverlayColour:
      typeof raw.imageOverlayColour === 'string' && raw.imageOverlayColour.trim().length > 0
        ? raw.imageOverlayColour.trim()
        : fallback.imageOverlayColour,
    imageOverlayOpacity: clampDecimal(raw.imageOverlayOpacity, fallback.imageOverlayOpacity, 0, 1, 0.05),
    imageTextGap: clampNumber(raw.imageTextGap, fallback.imageTextGap, 0, 40),
    titleDescriptionGap: clampNumber(raw.titleDescriptionGap, fallback.titleDescriptionGap, 0, 40),
    contentButtonGap: clampNumber(raw.contentButtonGap, fallback.contentButtonGap, 0, 40),
    titlePresetKey:
      typeof raw.titlePresetKey === 'string' && raw.titlePresetKey.trim().length > 0
        ? raw.titlePresetKey.trim()
        : fallback.titlePresetKey,
    descriptionPresetKey:
      typeof raw.descriptionPresetKey === 'string' && raw.descriptionPresetKey.trim().length > 0
        ? raw.descriptionPresetKey.trim()
        : fallback.descriptionPresetKey,
    cardPadding: clampNumber(raw.cardPadding, fallback.cardPadding, 8, 36),
    cardRadius: clampNumber(raw.cardRadius, fallback.cardRadius, 6, 32),
    descriptionLines: clampNumber(raw.descriptionLines, fallback.descriptionLines, 1, 6),
    textAlign:
      raw.textAlign === 'centre'
        ? 'centre'
        : raw.textAlign === 'left'
          ? 'left'
          : raw.textAlign === 'right'
            ? 'right'
            : fallback.textAlign,
    cardBackgroundColor:
      typeof raw.cardBackgroundColor === 'string' && raw.cardBackgroundColor.trim().length > 0
        ? raw.cardBackgroundColor.trim()
        : fallback.cardBackgroundColor,
    cardBorderColor:
      typeof raw.cardBorderColor === 'string' && raw.cardBorderColor.trim().length > 0
        ? raw.cardBorderColor.trim()
        : fallback.cardBorderColor,
    cardBorderWidth: clampNumber(raw.cardBorderWidth, fallback.cardBorderWidth, 0, 8),
    cardBorderStyle:
      raw.cardBorderStyle === 'dashed' || raw.cardBorderStyle === 'dotted' || raw.cardBorderStyle === 'solid'
        ? raw.cardBorderStyle
        : fallback.cardBorderStyle,
    cardShadowColour:
      typeof raw.cardShadowColour === 'string' && raw.cardShadowColour.trim().length > 0
        ? raw.cardShadowColour.trim()
        : fallback.cardShadowColour,
    cardShadowOpacity: clampDecimal(raw.cardShadowOpacity, fallback.cardShadowOpacity, 0, 1, 0.05),
    cardShadowX: clampNumber(raw.cardShadowX, fallback.cardShadowX, -48, 48),
    cardShadowY: clampNumber(raw.cardShadowY, fallback.cardShadowY, -48, 48),
    cardShadowBlur: clampNumber(raw.cardShadowBlur, fallback.cardShadowBlur, 0, 80),
    cardShadowSpread: clampNumber(raw.cardShadowSpread, fallback.cardShadowSpread, -40, 40),
    hoverEffectEnabled: raw.hoverEffectEnabled === true,
    hoverAnimation:
      raw.hoverAnimation === 'image-zoom' ||
      raw.hoverAnimation === 'glow' ||
      raw.hoverAnimation === 'reveal' ||
      raw.hoverAnimation === 'tilt' ||
      raw.hoverAnimation === 'lift-shadow'
        ? raw.hoverAnimation
        : fallback.hoverAnimation,
    hoverLiftY: clampNumber(raw.hoverLiftY, fallback.hoverLiftY, 0, 24),
    hoverShadowY: clampNumber(raw.hoverShadowY, fallback.hoverShadowY, -48, 64),
    hoverShadowBlur: clampNumber(raw.hoverShadowBlur, fallback.hoverShadowBlur, 0, 96),
    hoverShadowOpacity: clampDecimal(raw.hoverShadowOpacity, fallback.hoverShadowOpacity, 0, 1, 0.05),
    hoverImageZoomScale: clampDecimal(raw.hoverImageZoomScale, fallback.hoverImageZoomScale, 1, 1.2, 0.01),
    hoverImageRotateDeg: clampDecimal(raw.hoverImageRotateDeg, fallback.hoverImageRotateDeg, -12, 12, 0.5),
    hoverGlowColour:
      typeof raw.hoverGlowColour === 'string' && raw.hoverGlowColour.trim().length > 0
        ? raw.hoverGlowColour.trim()
        : fallback.hoverGlowColour,
    hoverGlowSize: clampNumber(raw.hoverGlowSize, fallback.hoverGlowSize, 8, 80),
    hoverGlowOpacity: clampDecimal(raw.hoverGlowOpacity, fallback.hoverGlowOpacity, 0, 1, 0.05),
    hoverRevealTarget:
      raw.hoverRevealTarget === 'description' || raw.hoverRevealTarget === 'buttons' || raw.hoverRevealTarget === 'both'
        ? raw.hoverRevealTarget
        : fallback.hoverRevealTarget,
    hoverRevealDistance: clampNumber(raw.hoverRevealDistance, fallback.hoverRevealDistance, 0, 32),
    hoverTiltDeg: clampDecimal(raw.hoverTiltDeg, fallback.hoverTiltDeg, -12, 12, 0.5),
    titleTypography: normalizeTypographyStyle(raw.titleTypography, fallback.titleTypography),
    descriptionTypography: normalizeTypographyStyle(raw.descriptionTypography, fallback.descriptionTypography),
    buttonTypography: normalizeTypographyStyle(raw.buttonTypography, fallback.buttonTypography),
    buttonStyle: normalizeButtonStyle(raw.buttonStyle, fallback.buttonStyle),
  };
}

export function normalizeCollectionCardTemplates(
  value: unknown
): CollectionCardTemplate[] {
  const rawTemplates = Array.isArray(value) ? value : [];
  const byId = new Map<string, CollectionCardTemplate>();
  DEFAULT_COLLECTION_CARD_TEMPLATES.forEach((template) => {
    byId.set(template.id, template);
  });

  rawTemplates.forEach((template, index) => {
    const fallback =
      DEFAULT_COLLECTION_CARD_TEMPLATES.find((entry) => entry.id === (template as Record<string, unknown>)?.id) ??
      DEFAULT_COLLECTION_CARD_TEMPLATES[Math.min(index, DEFAULT_COLLECTION_CARD_TEMPLATES.length - 1)] ??
      DEFAULT_COLLECTION_CARD_TEMPLATES[0];
    const normalized = normalizeCollectionCardTemplate(template, fallback);
    byId.set(normalized.id, normalized);
  });

  return Array.from(byId.values());
}

export function resolveCollectionCardTemplate(
  templates: CollectionCardTemplate[] | undefined,
  templateId?: string | null
): CollectionCardTemplate {
  const source = templates && templates.length > 0 ? templates : DEFAULT_COLLECTION_CARD_TEMPLATES;
  if (templateId) {
    const match = source.find((template) => template.id === templateId);
    if (match) return match;
  }
  return source[0] ?? DEFAULT_COLLECTION_CARD_TEMPLATES[0];
}

export function createCollectionCardTemplateId(name: string, existingIds?: Iterable<string>) {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const nextBase = base || `card-template-${Math.random().toString(36).slice(2, 8)}`;
  if (!existingIds) return nextBase;
  const used = new Set(existingIds);
  if (!used.has(nextBase)) return nextBase;
  let suffix = 2;
  while (used.has(`${nextBase}-${suffix}`)) suffix += 1;
  return `${nextBase}-${suffix}`;
}
